import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import { getSalaryEnabled } from 'selectors/addTeam';
import { getCanManageRoles } from 'selectors/session';

import { WAGE_TYPE_OPTIONS } from 'features/addTeam/constants';
import UpgradeWrapper from 'features/permissions/UpgradeWrapper';

import Box from 'components/Box';
import Text from 'components/Text';

import cxHelpers from 'util/className';

import RoleWageRow from '../RoleWageRow';

const DEFAULT_ROW_DATA = {
  id: null,
  wageRate: '',
  wageRole: '',
  wageType: WAGE_TYPE_OPTIONS[0].value,
  is_tipped: false,
  removed: false,
};

const ANNUAL_WAGE_TYPE = WAGE_TYPE_OPTIONS[1].value;

@connect((state, props) => ({
  canManage: getCanManageRoles(state, props),
  salaryEnabled: getSalaryEnabled(state, props),
}))
@cxHelpers('RolesBlock')
export default class RolesBlock extends PureComponent {
  static propTypes = {
    canManage: PropTypes.bool,
    salaryEnabled: PropTypes.bool.isRequired,
    onChange: PropTypes.func,
    editingRolesData: PropTypes.array,
    wageErrorBorder: PropTypes.bool,
    isEmployee: PropTypes.bool,
  };
  state = {
    rolesData:
      this.props.editingRolesData[0]?.wage_rate ||
      this.props.editingRolesData[0]?.role_name
        ? this.props.editingRolesData.map(roleData => ({
            id: roleData.id,
            wageRole: roleData.role_name,
            wageRate: roleData.wage_rate,
            wageType: roleData.wage_type,
            exiting: !!roleData.wage_rate,
            is_tipped: roleData.is_tipped,
            removed: false,
          }))
        : [DEFAULT_ROW_DATA],
  };

  buildWageAttributes = () => {
    const rolesData = this.state.rolesData.map(roleData => ({
      id: roleData.id,
      role_name: roleData.wageRole,
      wage_rate: roleData.wageRate,
      wage_type: roleData.wageType,
      is_tipped: roleData.is_tipped,
      _destroy: roleData.removed,
    }));

    if (rolesData[0].wageType === ANNUAL_WAGE_TYPE) {
      return [rolesData.splice(0, 1)];
    }

    return rolesData;
  };

  updateRolesData = (rolesData, callback) =>
    this.setState(
      {
        rolesData,
      },
      callback
    );

  handleAddRole = () => {
    const rolesData = [...this.state.rolesData];

    rolesData.push(DEFAULT_ROW_DATA);

    this.updateRolesData(rolesData);
  };

  handleChange = (idx, rowData) => {
    const rolesData = [...this.state.rolesData];
    rolesData[idx] = rowData;

    this.updateRolesData(rolesData, () =>
      this.props.onChange(this.buildWageAttributes())
    );
  };

  handleRemove = idx => {
    const rolesData = [...this.state.rolesData];

    // if it is an existing user, just flag it as removed
    // else, remove the row from the component state
    if (rolesData[idx].id != null) {
      rolesData[idx].removed = true;
      rolesData.splice(idx, 1, rolesData[idx]);
    } else {
      rolesData.splice(idx, 1);
    }

    this.updateRolesData(rolesData, () =>
      this.props.onChange(this.buildWageAttributes())
    );
  };

  wageTypeOptions = () =>
    this.props.salaryEnabled
      ? WAGE_TYPE_OPTIONS
      : WAGE_TYPE_OPTIONS.filter(option => option.value === 0);

  render() {
    const { rolesData } = this.state;
    const annualWage = rolesData[0]?.wageType === ANNUAL_WAGE_TYPE;

    return (
      <Box className={this.cx()}>
        {rolesData.map((rowData, idx) => (
          <RoleWageRow
            wageErrorBorder={this.props.wageErrorBorder}
            key={idx}
            rowIndex={idx}
            wageTypeOptions={this.wageTypeOptions()}
            showLabel={idx === 0}
            hideRow={(annualWage && idx !== 0) || rowData.removed}
            onChange={partial(this.handleChange, idx)}
            onRemove={partial(this.handleRemove, idx)}
            rowData={rowData}
            isEmployee={this.props.isEmployee}
          />
        ))}
        <Box>
          {!annualWage && (
            <UpgradeWrapper
              feature="role_wages"
              className={this.cxEl('paywall')}
              focusedFeature="laborCostManagement"
            >
              <Box
                mt12
                cursor="pointer"
                className={this.cxEl('add_role')}
                onClick={this.handleAddRole}
              >
                <Text
                  blue
                  fs14
                  fw600
                  i18n="add_team.individual_drawer.add_role_cta"
                />
              </Box>
            </UpgradeWrapper>
          )}
        </Box>
      </Box>
    );
  }
}
